/**
 * ====================================================================
 * FIKO CAD AREA DIMENSION
 * ====================================================================
 *
 * Yüzey alanı ölçüsü - Mesh/Face için alan hesaplar ve gösterir.
 * CATIA'nın "Measure Area" fonksiyonuna karşılık gelir.
 *
 * Özellikler:
 * - Üçgen bazlı alan hesaplama
 * - Ağırlık merkezi (centroid) gösterimi
 * - Normal vektör gösterimi (opsiyonel)
 * - Çoklu yüzey desteği
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    BaseDimensionOptions,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

// ====================================================================
// AREA DIMENSION OPTIONS
// ====================================================================

/**
 * Area dimension için özel seçenekler
 */
export interface AreaDimensionOptions extends BaseDimensionOptions {
    /** Normal vektör göster */
    showNormal?: boolean;
    /** Normal vektör uzunluğu */
    normalLength?: number;
    /** Centroid işareti göster */
    showCentroid?: boolean;
    /** Centroid işareti boyutu */
    centroidSize?: number;
    /** Alan birimi */
    areaUnit?: 'mm²' | 'cm²' | 'm²' | 'in²' | 'ft²';
    /** Birim dönüşüm faktörü */
    unitConversionFactor?: number;
    /** Label offset (centroid'den) */
    labelOffset?: number;
}

/**
 * Alan hesaplama sonucu
 */
export interface AreaCalculationResult {
    area: number;
    centroid: THREE.Vector3;
    normal: THREE.Vector3;
    triangleCount: number;
}

// ====================================================================
// AREA DIMENSION CLASS
// ====================================================================

/**
 * Area Dimension - Yüzey alanı ölçüsü
 */
export class AreaDimension extends Dimension {
    // Referans
    private ref: GeometryReference;
    private mesh: THREE.Mesh | null = null;

    // Hesaplanmış değerler
    private area: number = 0;
    private centroid: THREE.Vector3 = new THREE.Vector3();
    private surfaceNormal: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
    private triangleCount: number = 0;

    // Çizim elemanları
    private centroidMarker: THREE.Mesh | null = null;
    private normalArrow: THREE.Group | null = null;
    private label: THREE.Sprite | null = null;

    // Pre-allocated vectors (zero-allocation pattern)
    private readonly _v0 = new THREE.Vector3();
    private readonly _v1 = new THREE.Vector3();
    private readonly _v2 = new THREE.Vector3();
    private readonly _ab = new THREE.Vector3();
    private readonly _ac = new THREE.Vector3();
    private readonly _cross = new THREE.Vector3();
    private readonly _triangleNormal = new THREE.Vector3();
    private readonly _triangleCentroid = new THREE.Vector3();

    // Shared geometries
    private static _sharedCentroidGeometry: THREE.BufferGeometry | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // 🔴 SABİT BOYUT SİSTEMİ
    sabitBoyut = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // 🚀 PERFORMANS: Throttled updates
    private lastKameraUpdateTime: number = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS: number = 100;
    private targetScaleAdjustment: number = 1;
    private currentScaleAdjustment: number = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        ref: GeometryReference,
        options: AreaDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.LINEAR, options); // LINEAR as placeholder, area is special

        this.ref = ref;
        this.scene = scene;

        if (ref.mesh) {
            this.mesh = ref.mesh;
        }
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Mesh al
            if (!this.mesh) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Mesh tanımlı değil'
                    }
                };
            }

            // Alan hesapla
            const result = this.calculateAreaFromMesh(this.mesh);
            this.area = result.area;
            this.centroid.copy(result.centroid);
            this.surfaceNormal.copy(result.normal);
            this.triangleCount = result.triangleCount;

            // Değeri ayarla
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Yüzey alanı çok küçük veya hesaplanamadı'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // Sahneye hazırla
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Area dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Dimension'ı güncelle
     */
    update(): void {
        // Scale güncelleme event listener üzerinden yapılır
    }

    /**
     * Sahneye hazırlama
     */
    hazirla(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        try {
            if (this.label && this.label.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Sessizce devam et
        }
    }

    /**
     * Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) {
            return;
        }

        if (now - this.lastKameraUpdateTime < AreaDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, AreaDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * Kamera değişikliğinde scale güncelle
     */
    private kameraYonDegisti(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        if (this.sabitBoyut && this.label && this.label.userData.originalScale) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * Smooth scale interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.label || !this.label.userData.originalScale || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return this._value > DIMENSION_CONSTANTS.MIN_DISTANCE;
    }

    /**
     * Değeri hesapla
     */
    protected calculateValue(): number {
        const options = this._options as AreaDimensionOptions;
        const conversionFactor = options.unitConversionFactor || 1;
        return this.area * conversionFactor;
    }

    /**
     * Mesh'ten alan hesapla
     * @returns Area, centroid, normal ve üçgen sayısı
     */
    private calculateAreaFromMesh(mesh: THREE.Mesh): AreaCalculationResult {
        const geometry = mesh.geometry;
        const positions = geometry.getAttribute('position');
        const indices = geometry.getIndex();

        if (!positions) {
            return {
                area: 0,
                centroid: new THREE.Vector3(),
                normal: new THREE.Vector3(0, 0, 1),
                triangleCount: 0
            };
        }

        let totalArea = 0;
        const weightedCentroid = new THREE.Vector3();
        const avgNormal = new THREE.Vector3();
        let triangleCount = 0;

        const matrixWorld = mesh.matrixWorld;

        // Indexed geometry
        if (indices) {
            const count = indices.count;
            for (let i = 0; i < count; i += 3) {
                const i0 = indices.getX(i);
                const i1 = indices.getX(i + 1);
                const i2 = indices.getX(i + 2);

                // Vertex'leri al
                this._v0.fromBufferAttribute(positions, i0);
                this._v1.fromBufferAttribute(positions, i1);
                this._v2.fromBufferAttribute(positions, i2);

                // World space'e dönüştür
                this._v0.applyMatrix4(matrixWorld);
                this._v1.applyMatrix4(matrixWorld);
                this._v2.applyMatrix4(matrixWorld);

                // Alan hesapla (cross product)
                this._ab.subVectors(this._v1, this._v0);
                this._ac.subVectors(this._v2, this._v0);
                this._cross.crossVectors(this._ab, this._ac);

                const triangleArea = this._cross.length() / 2;
                totalArea += triangleArea;

                // Üçgen centroid
                this._triangleCentroid.set(
                    (this._v0.x + this._v1.x + this._v2.x) / 3,
                    (this._v0.y + this._v1.y + this._v2.y) / 3,
                    (this._v0.z + this._v1.z + this._v2.z) / 3
                );

                // Ağırlıklı centroid toplama
                weightedCentroid.addScaledVector(this._triangleCentroid, triangleArea);

                // Normal toplama
                this._triangleNormal.copy(this._cross).normalize();
                avgNormal.addScaledVector(this._triangleNormal, triangleArea);

                triangleCount++;
            }
        } else {
            // Non-indexed geometry
            const count = positions.count;
            for (let i = 0; i < count; i += 3) {
                this._v0.fromBufferAttribute(positions, i);
                this._v1.fromBufferAttribute(positions, i + 1);
                this._v2.fromBufferAttribute(positions, i + 2);

                this._v0.applyMatrix4(matrixWorld);
                this._v1.applyMatrix4(matrixWorld);
                this._v2.applyMatrix4(matrixWorld);

                this._ab.subVectors(this._v1, this._v0);
                this._ac.subVectors(this._v2, this._v0);
                this._cross.crossVectors(this._ab, this._ac);

                const triangleArea = this._cross.length() / 2;
                totalArea += triangleArea;

                this._triangleCentroid.set(
                    (this._v0.x + this._v1.x + this._v2.x) / 3,
                    (this._v0.y + this._v1.y + this._v2.y) / 3,
                    (this._v0.z + this._v1.z + this._v2.z) / 3
                );

                weightedCentroid.addScaledVector(this._triangleCentroid, triangleArea);

                this._triangleNormal.copy(this._cross).normalize();
                avgNormal.addScaledVector(this._triangleNormal, triangleArea);

                triangleCount++;
            }
        }

        // Normalize weighted values
        if (totalArea > 0) {
            weightedCentroid.divideScalar(totalArea);
            avgNormal.normalize();
        }

        return {
            area: totalArea,
            centroid: weightedCentroid,
            normal: avgNormal,
            triangleCount
        };
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as AreaDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const showNormal = options.showNormal !== false;
        const normalLength = options.normalLength || 5;
        const showCentroid = options.showCentroid !== false;
        const centroidSize = options.centroidSize || 0.3;
        const labelOffset = options.labelOffset || 3;

        // Centroid marker
        if (showCentroid) {
            this.centroidMarker = this.createCentroidMarker(this.centroid, color, centroidSize);
            this.centroidMarker.name = 'CentroidMarker';
            this.add(this.centroidMarker);
        }

        // Normal arrow
        if (showNormal) {
            this.normalArrow = this.createNormalArrow(
                this.centroid,
                this.surfaceNormal,
                color,
                normalLength
            );
            this.normalArrow.name = 'NormalArrow';
            this.add(this.normalArrow);
        }

        // Label pozisyonu (centroid'den offset)
        const labelPos = this.centroid.clone()
            .addScaledVector(this.surfaceNormal, labelOffset);

        this.label = this.createAreaLabel(labelPos, options);
        this.label.name = 'AreaLabel';
        this.add(this.label);
    }

    /**
     * Centroid marker oluştur
     */
    private createCentroidMarker(
        position: THREE.Vector3,
        color: number,
        size: number
    ): THREE.Mesh {
        if (!AreaDimension._sharedCentroidGeometry) {
            // Octahedron - centroid için uygun şekil
            AreaDimension._sharedCentroidGeometry = new THREE.OctahedronGeometry(1, 0);
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.8,
            toneMapped: false
        });

        const mesh = new THREE.Mesh(AreaDimension._sharedCentroidGeometry, material);
        mesh.position.copy(position);
        mesh.scale.setScalar(size);
        mesh.renderOrder = Infinity;
        mesh.frustumCulled = false;

        return mesh;
    }

    /**
     * Normal vektör oku oluştur
     */
    private createNormalArrow(
        position: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        length: number
    ): THREE.Group {
        const group = new THREE.Group();

        // Çizgi
        const endPoint = position.clone().addScaledVector(direction, length);
        const lineGeom = new LineGeometry();
        lineGeom.setPositions([
            position.x, position.y, position.z,
            endPoint.x, endPoint.y, endPoint.z
        ]);

        const lineMat = new LineMaterial({
            color: 0x0066FF, // Mavi normal
            linewidth: 1.5,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            fog: false,
            worldUnits: false,
            toneMapped: false
        });

        const line = new LineSegments2(lineGeom, lineMat);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();
        group.add(line);

        // Ok başı
        const arrowGeom = new THREE.ConeGeometry(0.2, 0.5, 8);
        arrowGeom.translate(0, -0.25, 0);

        const arrowMat = new THREE.MeshBasicMaterial({
            color: 0x0066FF,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            toneMapped: false
        });

        const arrow = new THREE.Mesh(arrowGeom, arrowMat);
        arrow.position.copy(endPoint);

        // Yönlendir
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;
        group.add(arrow);

        return group;
    }

    /**
     * Alan etiketi oluştur
     */
    private createAreaLabel(
        position: THREE.Vector3,
        options: AreaDimensionOptions
    ): THREE.Sprite {
        const fontSize = options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;
        const precision = options.precision || DIMENSION_CONSTANTS.DEFAULT_PRECISION;
        const areaUnit = options.areaUnit || 'mm²';

        // Format alan değeri
        const formattedArea = this._value.toFixed(precision);
        const text = `${formattedArea} ${areaUnit}`;

        // Canvas oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 20;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        // Arka plan
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.roundRect(4, 4, canvas.width - 8, canvas.height - 8, 6);
        ctx.fill();

        // Kenarlık
        ctx.strokeStyle = 'rgba(0, 100, 200, 0.5)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Metin
        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 2;

        ctx.fillStyle = '#0066FF'; // Mavi alan rengi
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        // Texture
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.scale.set(canvas.width * 0.012, canvas.height * 0.012, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        // Original scale sakla
        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        if (this.centroidMarker) {
            (this.centroidMarker.material as THREE.MeshBasicMaterial).color.setHex(color);
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        if (this.centroidMarker) {
            this.remove(this.centroidMarker);
            (this.centroidMarker.material as THREE.Material).dispose();
        }

        if (this.normalArrow) {
            this.normalArrow.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.geometry?.dispose();
                    (child.material as THREE.Material)?.dispose();
                }
                if (child instanceof LineSegments2) {
                    child.geometry.dispose();
                    (child.material as LineMaterial).dispose();
                }
            });
            this.remove(this.normalArrow);
        }

        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        this.centroidMarker = null;
        this.normalArrow = null;
        this.label = null;
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Alan değerini al
     */
    getArea(): number {
        return this.area;
    }

    /**
     * Centroid'i al
     */
    getCentroid(): THREE.Vector3 {
        return this.centroid.clone();
    }

    /**
     * Normal vektörü al
     */
    getNormal(): THREE.Vector3 {
        return this.surfaceNormal.clone();
    }

    /**
     * Üçgen sayısını al
     */
    getTriangleCount(): number {
        return this.triangleCount;
    }

    /**
     * Formatlanmış alan string'i al
     */
    getFormattedArea(): string {
        const precision = this._options.precision || 2;
        const options = this._options as AreaDimensionOptions;
        const areaUnit = options.areaUnit || 'mm²';
        return `${this._value.toFixed(precision)} ${areaUnit}`;
    }

    /**
     * Dispose
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
